import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import SDK from '../../../Components/SDK';
import logger from '../../../Helpers/logger';
import { requiredFieldError, validateAvatar } from '../../../Helpers/formValidation';
import { isAppOnline } from '../../../Helpers/Utility';
import { i18n } from '../../../localization/i18n';
import { T, TOAST } from '../../../localization/toastKeys';
import { useLanguage } from '../../../localization/useLanguage';
import { Dialog } from '../../../ui/Dialog';
import DialogFrame from '../../../ui/DialogFrame';
import DialogHead from '../../../ui/DialogFrame/DialogHead';
import DialogToaster from '../../../ui/DialogFrame/DialogToaster';
import { showDialogError, showOfflineError } from '../../../ui/DialogFrame/dialogToast';
import { DISAPPEARING_DEFAULT_DAYS, DISAPPEARING_OFF, GROUP_CREATE_ERROR } from '../constants';
import { getContactName } from '../../../Components/WebChat/Common/ContactList/contactHelpers';
import useContactSearch from '../../../Components/WebChat/Common/ContactList/useContactSearch';
import DetailsStep from './DetailsStep';
import MembersStep from './MembersStep';

const STEP = { MEMBERS: 'members', DETAILS: 'details' };

const INITIAL_DETAILS = {
    name: '',
    description: '',
    logoFile: null,
    logoPreview: '',
    isDisappearing: false,
    durationDays: DISAPPEARING_DEFAULT_DAYS,
};

// Singular and plural sentences, since the verb agrees with the user count.
const BLOCK_ERROR_KEYS = {
    [GROUP_CREATE_ERROR.BLOCKED_YOU]: { one: 'createChat.ERR_BLOCKED_YOU', many: 'createChat.ERR_BLOCKED_YOU_MANY' },
    [GROUP_CREATE_ERROR.BLOCKED_BY_ADMIN]: {
        one: 'createChat.ERR_BLOCKED_BY_ADMIN',
        many: 'createChat.ERR_BLOCKED_BY_ADMIN_MANY',
    },
};

// Beyond this many names the rest are counted as "N other user(s)".
const MAX_LISTED_NAMES = 2;

// The server lists offending members by JID; the toast shows their list names.
// The separator is translated, since Arabic uses "،" rather than ",".
const namesForJids = (jids, selected) => {
    const names = jids.map(jid => {
        const userId = String(jid).split('@')[0];
        const contact = selected.get(userId);
        return contact ? getContactName(contact) : userId;
    });
    const separator = i18n.t('createChat.NAME_SEPARATOR');
    if (names.length <= MAX_LISTED_NAMES) return names.join(separator);
    return i18n.t('createChat.NAMES_AND_OTHERS', {
        names: names.slice(0, MAX_LISTED_NAMES).join(separator),
        count: names.length - MAX_LISTED_NAMES,
    });
};

/**
 * New Group pop-up (FR60 screens 60.3 and 60.4). The draft lives here, so it survives a
 * detour through New Contact while the pop-up is only hidden.
 *
 * @param {boolean} open Whether the pop-up is shown.
 * @param {Function} onClose Discards the draft and returns to the chat list.
 * @param {Function} onNewContact Opens the New Contact pop-up over this one.
 * @param {Function} onCreated Called with { groupId, groupName } once the group exists.
 */
const NewGroupDialog = ({ open, onClose, onNewContact, onCreated }) => {
    const { t } = useLanguage();
    const [step, setStep] = useState(STEP.MEMBERS);
    const [selected, setSelected] = useState(() => new Map());
    const [details, setDetails] = useState(INITIAL_DETAILS);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const search = useContactSearch(open && step === STEP.MEMBERS);

    useEffect(() => () => details.logoPreview && URL.revokeObjectURL(details.logoPreview), [details.logoPreview]);

    const handleToggle = contact =>
        setSelected(previous => {
            const next = new Map(previous);
            if (next.has(contact.userId)) next.delete(contact.userId);
            else next.set(contact.userId, contact);
            return next;
        });

    const handleDetailsChange = patch => {
        setDetails(previous => ({ ...previous, ...patch }));
        if ('name' in patch) setErrors(previous => ({ ...previous, name: '' }));
    };

    const handleLogoPick = file => {
        const logoError = validateAvatar(file);
        setErrors(previous => ({ ...previous, logo: logoError }));
        if (logoError) return;
        setDetails(previous => ({ ...previous, logoFile: file, logoPreview: URL.createObjectURL(file) }));
    };

    const handleNameBlur = () => {
        if (!details.name.trim()) {
            setErrors(previous => ({ ...previous, name: requiredFieldError(t('createChat.GROUP_NAME')) }));
        }
    };

    const handleCreate = async () => {
        if (isSubmitting) return;
        if (!isAppOnline()) {
            showOfflineError();
            return;
        }
        const groupName = details.name.trim();
        const participants = [...selected.values()].map(contact => contact.jid);
        const msgDisappear = details.isDisappearing ? details.durationDays : DISAPPEARING_OFF;

        setIsSubmitting(true);
        const response = await SDK.createGroup(
            groupName,
            participants,
            details.logoFile || '',
            false,
            details.description.trim(),
            { msgDisappear },
        );
        setIsSubmitting(false);

        if (response?.statusCode === 200 && response.data?.groupId) {
            onCreated({ groupId: response.data.groupId, groupName });
            return;
        }
        const blockKeys = BLOCK_ERROR_KEYS[response?.errorCode];
        if (blockKeys) {
            const users = response.users || [];
            const messageKey = users.length > 1 ? blockKeys.many : blockKeys.one;
            showDialogError(t(messageKey, { name: namesForJids(users, selected) }));
            return;
        }
        logger.error('Create group failed', { statusCode: response?.statusCode, errorCode: response?.errorCode });
        showDialogError(t(`${TOAST}.${T.ACTION_FAILED_RETRY}`, { action: t('createChat.ACTION_GROUP_CREATION') }));
    };

    const isMembersStep = step === STEP.MEMBERS;

    return (
        <Dialog open={open} onOpenChange={isOpen => !isOpen && open && onClose()}>
            <DialogFrame>
                <DialogToaster open={open} />
                <DialogHead
                    title={t(isMembersStep ? 'createChat.NEW_GROUP_TITLE' : 'createChat.GROUP_DETAILS_TITLE')}
                    onClose={onClose}
                />

                {isMembersStep ? (
                    <MembersStep
                        search={search}
                        selected={selected}
                        onToggle={handleToggle}
                        onNewContact={onNewContact}
                        onContinue={() => setStep(STEP.DETAILS)}
                    />
                ) : (
                    <DetailsStep
                        details={details}
                        errors={errors}
                        selected={selected}
                        isSubmitting={isSubmitting}
                        onChange={handleDetailsChange}
                        onLogoPick={handleLogoPick}
                        onNameBlur={handleNameBlur}
                        onRemoveMember={handleToggle}
                        onBack={() => setStep(STEP.MEMBERS)}
                        onCreate={handleCreate}
                    />
                )}
            </DialogFrame>
        </Dialog>
    );
};

NewGroupDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onNewContact: PropTypes.func.isRequired,
    onCreated: PropTypes.func.isRequired,
};

export default NewGroupDialog;
